import {
	WORKFRONT_LOOKUP_LABELS,
	WorkfrontBundleObjectRole,
	WorkfrontComputeFn,
	WorkfrontLookup,
	WorkfrontMappingRule,
	WorkfrontSourceKind,
	WorkfrontTargetType,
} from '@atlas/types'
import { FieldShell, SelectField } from '@atlas/ui-components'
import { Checkbox, HelpText, Input, Switch, Textarea } from '@atlas/ui-primitives'
import type { FieldRow } from '@/services/workfront-mappings.service'
import { humanize } from '../../components/StatusBadges'
import { type FormProps, LEVEL_OF_ROLE, targetOptionLabel } from './drawer-context'

const options = (values: readonly string[]) => values.map((value) => ({ value, label: humanize(value) }))

/** A validation message under a picker, which renders its own label. */
export function ErrorLine({ message }: { message: string | undefined }) {
	return message ? <HelpText variant="error">{message}</HelpText> : null
}

/** The rule's parameters that apply to it: the lookup or axis, the computation, or the first value. */
function RuleParams({ row, onChange, errors }: Pick<FormProps<FieldRow>, 'row' | 'onChange' | 'errors'>) {
	const params = row.ruleParams
	const set = (patch: FieldRow['ruleParams']) => onChange({ ruleParams: { ...params, ...patch } })
	if (row.rule === WorkfrontMappingRule.LOOKUP && !params.axis) {
		return (
			<SelectField
				label="Lookup"
				placeholder="choose"
				value={params.lookup ?? ''}
				options={Object.values(WorkfrontLookup).map((value) => ({ value, label: WORKFRONT_LOOKUP_LABELS[value] }))}
				onChange={(lookup) => set({ lookup: (lookup || undefined) as WorkfrontLookup | undefined })}
			/>
		)
	}
	if (row.rule === WorkfrontMappingRule.LOOKUP) {
		return <p className="text-ds-sm text-ds-text-muted">Through the value mappings of the {params.axis} axis.</p>
	}
	if (row.rule === WorkfrontMappingRule.COMPUTE) {
		return (
			<div>
				<SelectField
					label="Computation"
					placeholder="choose"
					value={params.fn ?? ''}
					options={options(Object.values(WorkfrontComputeFn))}
					onChange={(fn) => set({ fn: (fn || undefined) as WorkfrontComputeFn | undefined })}
				/>
				<ErrorLine message={errors.ruleParams} />
			</div>
		)
	}
	if (row.rule === WorkfrontMappingRule.DIRECT) {
		return (
			<Checkbox isSelected={params.firstValue ?? false} onChange={(firstValue) => set({ firstValue })}>
				Keep only the first value of a multiple choice
			</Checkbox>
		)
	}
	return null
}

/** Where a field mapping reads: a custom field (with its legacy fallback) or a standard Workfront field. */
function SourcePickers({ row, onChange, errors, context }: FormProps<FieldRow>) {
	if (row.sourceKind === WorkfrontSourceKind.STANDARD) {
		return (
			<FieldShell label="Standard field" htmlFor="field-source" error={errors.sourceField}>
				<Input
					id="field-source"
					value={row.sourceField ?? ''}
					placeholder="plannedCompletionDate"
					aria-invalid={!!errors.sourceField}
					onChange={(event) => onChange({ sourceField: event.target.value || null })}
				/>
			</FieldShell>
		)
	}
	return (
		<>
			<div>
				<SelectField
					label="Workfront field"
					placeholder="choose"
					value={row.sourceParameterId ?? ''}
					options={context.parameters}
					isSearchable
					onChange={(id) => onChange({ sourceParameterId: id || null })}
				/>
				<ErrorLine message={errors.sourceParameterId ?? errors.sourceKind} />
			</div>
			<div>
				<SelectField
					label="Fallback field"
					value={row.fallbackParameterId ?? ''}
					placeholder="none"
					options={context.parameters}
					isSearchable
					onChange={(id) => onChange({ fallbackParameterId: id || null })}
				/>
				<ErrorLine message={errors.fallbackParameterId} />
			</div>
		</>
	)
}

/**
 * A field mapping: the source (role, field, fallback), the target from the
 * catalogue at the role's level, the rule and its parameters, enabled and a note.
 * Choosing an axis target with the lookup rule fills the axis in.
 */
export function FieldMappingForm(props: FormProps<FieldRow>) {
	const { row, onChange, errors, context } = props
	const level = LEVEL_OF_ROLE[row.sourceRole]
	const targets = context.catalogue.filter((target) => target.level === level && !target.structural)
	const pickTarget = (path: string) => {
		const target = targets.find((candidate) => candidate.path === path)
		const axis = target?.type === WorkfrontTargetType.AXIS ? target.axis : undefined
		onChange({
			targetPath: path,
			...(axis ? { rule: WorkfrontMappingRule.LOOKUP, ruleParams: { ...row.ruleParams, axis } } : {}),
		})
	}
	return (
		<div className="space-y-4">
			<div className="grid gap-4 sm:grid-cols-2">
				<SelectField
					label="Source role"
					placeholder="choose"
					value={row.sourceRole}
					isClearable={false}
					options={options(Object.values(WorkfrontBundleObjectRole))}
					onChange={(role) => onChange({ sourceRole: role as WorkfrontBundleObjectRole })}
				/>
				<SelectField
					label="Source kind"
					placeholder="choose"
					value={row.sourceKind}
					isClearable={false}
					options={options(Object.values(WorkfrontSourceKind))}
					onChange={(kind) => onChange({ sourceKind: kind as WorkfrontSourceKind })}
				/>
			</div>
			<SourcePickers {...props} />
			<div>
				<SelectField
					label="ATLAS target"
					placeholder="choose"
					value={row.targetPath}
					options={targets.map((target) => ({ value: target.path, label: targetOptionLabel(target) }))}
					isSearchable
					onChange={pickTarget}
				/>
				<ErrorLine message={errors.targetPath} />
			</div>
			<div>
				<SelectField
					label="Rule"
					placeholder="choose"
					value={row.rule}
					isClearable={false}
					options={options(Object.values(WorkfrontMappingRule))}
					onChange={(rule) => onChange({ rule: rule as WorkfrontMappingRule })}
				/>
				<ErrorLine message={errors.rule} />
			</div>
			<RuleParams row={row} onChange={onChange} errors={errors} />
			<Switch isSelected={row.enabled} onChange={(enabled) => onChange({ enabled })}>
				Enabled
			</Switch>
			<ErrorLine message={errors.enabled} />
			<FieldShell label="Note" htmlFor="field-notes" error={errors.notes}>
				<Textarea
					id="field-notes"
					value={row.notes ?? ''}
					onChange={(event) => onChange({ notes: event.target.value || null })}
				/>
			</FieldShell>
		</div>
	)
}
